import { DateTime } from 'luxon';

import { inferDateTime } from './infer-date';

export class InvalidDateError extends Error {}

interface SafelyConvertDateOptions {
  /**
   * An optional error message that should be used when an {@link Error} is thrown in the case
   * that the value is invalid or cannot be converted to a valid {@link DateTime} instance.
   */
  readonly errorMessage?: string;
  /**
   * Determines whether or not the value should be returned as a {@link Date} instance or a
   * {@link DateTime} instance, in the case that it is valid and can be safely converted to a
   * date-like form.
   *
   * @default "DateTime"
   */
  readonly returnAs?: 'Date' | 'DateTime';
  /**
   * Determines whether or not an {@link Error} should be thrown in the case that the value is
   * invalid or cannot be converted to a valid {@link DateTime} instance.  If 'false', a
   * {@link null} value will be returned in the case that the value is invalid.
   *
   * @default false
   */
  readonly strict?: boolean;
}

type SafelyConvertToDateForm<O extends SafelyConvertDateOptions> = O extends { returnAs: 'Date' }
  ? Date
  : DateTime<true>;

type SafelyConvertedDate<O extends SafelyConvertDateOptions> = O extends { strict: true }
  ? SafelyConvertToDateForm<O>
  : null | SafelyConvertToDateForm<O>;

/**
 * Safely converts a value of type {@link unknown} to a {@link DateTime} or {@link Date} instance,
 * either throwing an {@link Error} or returning {@link null} if an invalid value is encountered.
 *
 * @param {unknown} value
 *   The value that should be converted to a valid {@link DateTime} or {@link Date} instance.
 * @param {O} options The options that control the behavior of the conversion.
 *
 * @returns {SafelyConvertedDate<O>}
 *   The {@link DateTime}, {@link Date} instance, or {@link null}, depending on the options
 *   {@link SafelyConvertDateOptions} provided.
 *
 * @template {O} options The options for the conversion.
 */
export const safelyConvertDate = <O extends SafelyConvertDateOptions>(
  value: unknown,
  options?: O,
): SafelyConvertedDate<O> => {
  const returnOrThrow = (message: string) => {
    if (options?.strict) {
      throw new InvalidDateError(options.errorMessage ?? message);
    }
    return null as SafelyConvertedDate<O>;
  };

  let dt: DateTime | undefined = undefined;
  if (typeof value === 'string') {
    dt = inferDateTime(value);
  } else if (value instanceof Date) {
    dt = DateTime.fromJSDate(value);
  } else if (DateTime.isDateTime(value)) {
    dt = value;
  } else if (typeof value === 'number') {
    dt = DateTime.fromMillis(value);
  }

  if (!dt) {
    return returnOrThrow('The provided value cannot be converted to a valid DateTime instance.');
  } else if (!dt.isValid) {
    const valueString = typeof value === 'string' ? value : JSON.stringify(value);
    return returnOrThrow(
      `The provided value '${valueString}' cannot be converted to a valid DateTime instance.`,
    );
  } else if (options?.returnAs === 'Date') {
    return dt.toJSDate() as SafelyConvertedDate<O>;
  }
  return dt as SafelyConvertedDate<O>;
};
